<script setup>

import {computed, onBeforeMount, ref} from "vue";
import axios from "@/utils/axios.js";
import {useSessionStore} from "@/utils/useSessionStore.js";
import {useRouter} from "vue-router";
import {__} from "@/i18n/useLanguage.js";

const router = useRouter();
import CwIcon from "@/components/form/CwIcon.vue";
import MobileMyPageHeader from "@/components/ui/MobileMyPageHeader.vue";
import MobileSidebarAllProductsAccordion from "@/components/ui/MobileSidebarAllProductsAccordion.vue";
import appStoreBadge from "@/assets/app-stores/download-on-the-app-store.svg";
import googlePlayBadge from "@/assets/app-stores/get-it-on-google-play.png";

const sessionStore = useSessionStore();

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
})

function loginOrMypage() {
  if (sessionStore.authenticated) goTo('/my-page')
  else goTo('/login')

}

function logoutOrRegister() {
  if (sessionStore.authenticated) logout()
  else goTo('/register')
}

function goTo(url) {
  emit('update:visible', false)
  router.push(url);
}

async function logout() {
  emit('update:visible', false)
}

onBeforeMount(async () => {
  const res = await getCategories();
  categories.value = res.data.data;
})

const getCategories = async () => {
  return await axios.get('/api/evans-cart/categories-service')
}
const categories = ref([])
const rootMenus = computed(() => {
  return categories.value.filter((item) => item.id !== 12);
})

const saleMenus = computed(() => {
  if (categories.value.length === 0) return [];

  return categories.value.filter((item) => item.id === 12)?.[0]?.descendants || [];
});

const emit = defineEmits(["update:visible"]);

function closeSidebar() {
  emit('update:visible', false)
}

const keyword = ref('');
const submitKeyword = () => {
  if (keyword.value.length === 0) return;
  goTo({
    name: 'ProductSearch',
    query: {
      keyword: keyword.value
    }
  })
}

const customLinks = [
  {label: 'CW 커스텀 블렌드', to: {name: 'CustomBlendService'}},
  {label: 'CW 커스텀 프래그런스', to: {name: 'CustomLanding'}},
  {label: 'CW 커스텀 향수용기', to: {name: 'CustomBottle'}},
  {label: 'OEM/ODM 문의', to: '/customer/manufacturing-inquiry/board', accent: true},
];

</script>
<template>
  <transition
    class="transition-all duration-300 print:hidden"
    enter-from-class="opacity-0 -left-full"
    enter-to-class="opacity-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0 -left-full"
  >
    <div
      v-show="props.visible"
      class="fixed w-full top-0 left-0 max-h-screen z-[200] overflow-auto max-w-md mx-auto"
    >
      <div class="relative max-w-lg mx-auto">
        <div
          class="w-full px-4 py-4 bg-white border-b border-stone-200 flex-col justify-start items-start gap-2 inline-flex sticky top-0 left-0 z-20"
        >
          <div class="self-stretch justify-start items-center gap-4 inline-flex">
            <div class="grow shrink basis-0 min-h-8 justify-start items-center flex">
              <img
                class="w-14"
                src="/images/icons/logo-main.png"
                alt=""
              >
            </div>
            <div class="menu-icons justify-end items-center gap-4 flex">
              <img
                src="/images/icons/close.png"
                class="w-5 h-5 relative"
                @click="closeSidebar"
              >
            </div>
          </div>
        </div>
        <div class="z-10 w-full bg-white">
          <div class="w-full px-4 py-5 flex-col justify-start items-start gap-5 inline-flex">
            <mobile-my-page-header
              v-if="sessionStore.authenticated"
              @confirm-logout="logout"
            />
            <div
              v-else
              class="w-full border-y border-stone-300 bg-white px-4 py-4 text-stone-800"
            >
              <div
                class="flex items-center justify-between gap-3 w-full"
              >
                <button
                  class="flex gap-1"
                  @click="loginOrMypage"
                >
                  <img
                    src="/images/icons/user-white.png"
                    class="w-5 h-5"
                  >
                  <div class="text-sm font-medium">
                    {{ $t("로그인") }}
                  </div>
                </button>
                <button
                  class=" inline-flex items-center gap-1  border-stone-300 px-3 py-2 text-sm text-stone-700"
                  @click="logoutOrRegister"
                >
                  <cw-icon
                    name="heart"
                    class="w-5"
                  />
                  {{ $t('회원가입') }}
                </button>
                <button
                  class="inline-flex items-center gap-1  border-stone-300 px-3 py-2 text-sm text-stone-700"
                  @click="()=>goTo({name:'CustomerCenter'})"
                >
                  <cw-icon name="phone" />
                  {{ $t("고객센터") }}
                </button>
              </div>
            </div>
            <div class="w-full border border-stone-300 bg-white px-4 py-3">
              <div class="flex items-center gap-3">
                <input
                  v-model="keyword"
                  class="grow shrink basis-0 cw-ebony-color placeholder-stone-400 text-base font-normal leading-none bg-transparent"
                  :placeholder="$t('검색어를 입력하세요.')"
                  @change="()=>{submitKeyword()}"
                >
                <img
                  class="w-5 h-5 relative"
                  src="/images/icons/search.svg"
                  alt=""
                  @click="submitKeyword"
                >
              </div>
            </div>
            <section class="w-full">
              <div
                class="mobile-sidebar-hero"
              >
                <div class="ev-heading-2 text-stone-800">
                  {{ $t("Custom") }}
                </div>
                <div class="text-sm text-stone-600">
                  {{ $t("브랜드에 맞춘 향과 제품을 빠르게 탐색해보세요.") }}
                </div>
              </div>
              <div class="grid grid-cols-1 divide-y divide-stone-300 border-b border-stone-300">
                <button
                  v-for="item in customLinks"
                  :key="item.label"
                  class="mobile-sidebar-link"
                  @click="()=>goTo(item.to)"
                >
                  <span>{{ $t(item.label) }}</span>
                  <img
                    src="/images/icons/chevron.svg"
                    alt=""
                    class="h-4 w-4"
                  >
                </button>
              </div>
            </section>

            <mobile-sidebar-all-products-accordion
              id="all-products"
              :root-menus="rootMenus"
              :sale-menus="saleMenus"
              @moved="()=>{ closeSidebar() }"
              @open-sale-overview="()=>{
                router.push({
                  name: 'Category',
                  params: {
                    categoryId: 12
                  }
                })
                closeSidebar();
              }"
              @navigate="goTo"
            />
            <section class="mobile-sidebar-card">
              <div class="mobile-sidebar-card-title">
                {{ $t("Sales") }}
              </div>
              <div class="grid grid-cols-2 divide-y border-y divide-stone-300 border-stone-300">
                <button
                  class="mobile-sidebar-tile col-span-2 text-left"
                  @click="()=>{
                    router.push({
                      name: 'Category',
                      params: {
                        categoryId: 12
                      }
                    })
                    closeSidebar();
                  }"
                >
                  {{ $t("Sale 전체보기") }}
                </button>
                <button
                  v-for="(item, index) in saleMenus"
                  :key="item.id"
                  class="mobile-sidebar-subtile odd:border-l"
                  :class="saleMenus.length % 2 === 1 && index === saleMenus.length - 1 ? 'col-span-2' : ''"
                  @click="()=>goTo({
                    name: 'Category',
                    params: {
                      categoryId: item.id
                    }
                  })"
                >
                  {{ __(item.name, item.name_en) }}
                </button>
              </div>
            </section>
            <section class="mobile-sidebar-card">
              <div class="mobile-sidebar-card-title">
                {{ $t("Specials") }}
              </div>
              <div class="grid grid-cols-2 divide-stone-300 border-y border-stone-300">
                <button
                  class="mobile-sidebar-tile text-left border-b border-r border-stone-300"
                  @click="()=>{ goTo({name: 'ProductNewItem'})}"
                >
                  WHAT'S NEW
                </button>
                <button
                  class="mobile-sidebar-tile text-left border-b border-stone-300"
                  @click="()=>{ goTo({name: 'BestSeller'})}"
                >
                  BEST SELLER
                </button>
                <button
                  class="mobile-sidebar-tile text-left border-r"
                  @click="()=>{ goTo({name: 'Promotion'})}"
                >
                  PROMOTION
                </button>
                <button
                  class="mobile-sidebar-tile text-left"
                  @click="()=>{ goTo({name: 'Diy'})}"
                >
                  CONTENTS
                </button>
              </div>
            </section>

            <section class="mobile-sidebar-card">
              <div class="mobile-sidebar-card-title">
                {{ $t("The School of Scent & Art") }}
              </div>
              <div class="grid grid-cols-2 border-y border-stone-300">
                <button
                  class="mobile-sidebar-subtile border-b border-r"
                  @click="()=>goTo({name:'TssaStudio'})"
                >
                  {{ $t("TSSA 안내") }}
                </button>
                <button
                  class="mobile-sidebar-subtile border-b"
                  @click="()=>goTo({name:'TssaCategoryShow'})"
                >
                  {{ $t("수강신청") }}
                </button>
                <button
                  class="mobile-sidebar-subtile border-r"
                  @click="()=>goTo({name:'TssaBooks'})"
                >
                  {{ $t("수업교재") }}
                </button>
                <button
                  class="mobile-sidebar-subtile"
                  @click="()=>goTo({name:'TssaNotice'})"
                >
                  {{ $t("공지사항") }}
                </button>
              </div>
            </section>

            <section class="mobile-sidebar-card">
              <div
                class="mobile-sidebar-card-title"
                @click="()=>goTo({name:'CustomerCenter'})"
              >
                {{ $t("Customer Service") }}
              </div>
              <div class="grid grid-cols-2 border-y border-stone-300">
                <button
                  class="mobile-sidebar-subtile border-b border-r"
                  @click="()=>goTo({name:'CustomerProductReview'})"
                >
                  {{ $t("상품후기") }}
                </button>
                <button
                  class="mobile-sidebar-subtile border-b"
                  @click="()=>goTo({name:'CustomerProductInquiry'})"
                >
                  {{ $t("상품문의") }}
                </button>
                <button
                  class="mobile-sidebar-subtile border-b border-r"
                  @click="()=>goTo({name:'CustomerFaq'})"
                >
                  {{ $t("자주묻는질문") }}
                </button>
                <button
                  class="mobile-sidebar-subtile border-b"
                  @click="()=>goTo({name:'CustomerCenter'})"
                >
                  {{ $t("주문/배송문의") }}
                </button>
                <button
                  class="mobile-sidebar-subtile border-b border-r"
                  @click="()=>goTo({name:'OfflineStore'})"
                >
                  {{ $t("매장안내") }}
                </button>
                <button
                  class="mobile-sidebar-subtile border-b"
                  @click="()=>goTo({name:'CustomerWholesaleApply'})"
                >
                  {{ $t("도매신청") }}
                </button>
                <button
                  class="mobile-sidebar-subtile col-span-2"
                  @click="()=>goTo({name:'DealerApply'})"
                >
                  {{ $t("딜러모집") }}
                </button>
              </div>
            </section>
            <div class="justify-center items-start gap-3 inline-flex px-1 bg-stone-100 w-full p-4">
              <div class="w-7 h-7 relative">
                <img
                  src="/images/icons/sns-insta.svg"
                  alt=""
                >
              </div>
              <div class="w-7 h-7 relative">
                <img
                  src="/images/icons/sns-kakao.svg"
                  alt=""
                >
              </div>
              <div class="w-7 h-7 relative">
                <img
                  src="/images/icons/sns-facebook.svg"
                  alt=""
                >
              </div>
              <div class="w-7 h-7 relative">
                <img
                  src="/images/icons/sns-youtube.svg"
                  alt=""
                >
              </div>
              <a
                href="https://apps.apple.com/kr/app/chromaworks/id6479591889"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="$t('App Store에서 설치')"
                class="inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500 focus:ring-offset-2"
              >
                <img
                  :src="appStoreBadge"
                  :alt="$t('Download on the App Store')"
                  class="block h-7 w-auto"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.evanskim.chromaworks"
                target="_blank"
                rel="noopener noreferrer"
                :aria-label="$t('Google Play에서 설치')"
                class="inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-stone-500 focus:ring-offset-2"
              >
                <img
                  :src="googlePlayBadge"
                  :alt="$t('Get it on Google Play')"
                  class="block h-7 w-auto"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.menu-icons img {
  cursor: pointer;
}

.mobile-sidebar-card {
  @apply w-full bg-white py-4 flex flex-col gap-3;
}

.mobile-sidebar-card-title {
  @apply text-xs font-semibold uppercase tracking-[0.18em] text-stone-400 pl-4;
}

.mobile-sidebar-hero {
  @apply bg-stone-100 p-4 flex flex-col gap-2;
}

.mobile-sidebar-link {
  @apply w-full px-4 py-3 text-left text-stone-700 text-base font-medium flex items-center justify-between transition-colors duration-200;
}

.mobile-sidebar-tile {
  @apply px-4 py-3 text-sm text-stone-700 transition-colors duration-200;
}

.mobile-sidebar-subtile {
  @apply bg-white px-4 py-3 text-sm font-medium text-stone-600 text-left;
}

</style>
